import { useState, useEffect, useRef } from "react";
import { SkeletonList } from "@/components/ui/pageSkeletons";
import { useTranslation } from "react-i18next";
import { Bell, Check, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NotificationItem } from "./NotificationItem";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface NotificationDropdownProps {
  collapsed?: boolean;
}

export function NotificationDropdown({ collapsed }: NotificationDropdownProps) {
  const { t } = useTranslation();
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [isWiggling, setIsWiggling] = useState(false);
  const wiggleInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const {
    notifications,
    isLoading,
    unreadCount,
    hasNewNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    isMarkingAllRead,
    isDeletingAll,
    getRelativeTime,
  } = useNotifications();

  // Periodic wiggle when there are unread notifications
  useEffect(() => {
    if (unreadCount > 0) {
      setIsWiggling(true);
      const timeout = setTimeout(() => setIsWiggling(false), 600);
      wiggleInterval.current = setInterval(() => {
        setIsWiggling(true);
        setTimeout(() => setIsWiggling(false), 600);
      }, 4000);
      return () => {
        clearTimeout(timeout);
        if (wiggleInterval.current) clearInterval(wiggleInterval.current);
      };
    } else {
      setIsWiggling(false);
      if (wiggleInterval.current) clearInterval(wiggleInterval.current);
    }
  }, [unreadCount]);

  const handleDeleteAll = () => {
    deleteAllNotifications();
    setConfirmDeleteAll(false);
    toast.success(t("notifications.allDeleted"));
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9 rounded-full transition-all duration-200 hover:bg-primary/10"
          >
            <Bell className={cn("h-5 w-5 transition-transform", isWiggling && "animate-wiggle")} />
            {/* Badge — always visible */}
            <span
              className={cn(
                "absolute -top-0.5 -right-0.5 h-5 min-w-[1.25rem] px-1 rounded-full text-xs font-bold flex items-center justify-center transition-all duration-200",
                unreadCount > 0
                  ? "bg-white text-primary"
                  : "bg-muted text-muted-foreground scale-90",
                hasNewNotification && "animate-bounce-in"
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
            {/* Green activity dot */}
            {hasNewNotification && (
              <span className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-background animate-pulse" />
            )}
            <span className="sr-only">{t("notifications.title")}</span>
          </Button>
        </PopoverTrigger>
        
        <PopoverContent
          className="w-[calc(100vw-32px)] sm:w-[420px] p-0 rounded-[22px] border-[#E4EDF5] shadow-[0_20px_50px_-16px_rgba(17,49,56,0.32)] overflow-hidden"
          align={collapsed ? "start" : "end"}
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
        >
          {/* Cabecera con la misma pastilla de icono que el resto de la app y el
              recuento de pendientes leyéndose de un vistazo. */}
          <div className="px-4 py-3.5 border-b border-[#EEF4FA] bg-white">
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                style={{ backgroundColor: "#2B7DE0", boxShadow: "0 6px 14px #2B7DE042, inset 0 1px 0 rgba(255,255,255,0.28)" }}
              >
                <Bell className="w-[18px] h-[18px]" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-[15px] leading-tight text-foreground">{t("notifications.title")}</h3>
                {/* `n` y no `count`: con `count` i18next entra en resolución de
                    plurales y buscaría claves _one/_other que no existen. La frase
                    es invariable en los cuatro idiomas, así que no hacen falta. */}
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  {unreadCount > 0
                    ? t("notifications.unreadCount", { n: unreadCount, defaultValue: "{{n}} sin leer" })
                    : t("notifications.allCaughtUp", "Todo al día")}
                </p>
              </div>
            </div>

            {notifications.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                {/* Secundario: gris macizo y QUIETO — repite fondo y color en
                    :hover para anular el `hover:bg-accent` del variant ghost, que
                    en escritorio lo hacía parpadear al pasar por encima. */}
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 flex-1 rounded-lg text-[12px] font-semibold bg-slate-200 text-slate-700 hover:bg-slate-200 hover:text-slate-700"
                    onClick={() => markAllAsRead()}
                    disabled={isMarkingAllRead}
                  >
                    {isMarkingAllRead ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {t("notifications.markAllRead")}
                  </Button>
                )}
                {/* Rojo pleno del `destructive` de la app — el mismo botón
                    «Eliminar» de Plagas, no una versión aguada. */}
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8 flex-1 rounded-lg text-[12px] font-semibold"
                  onClick={() => setConfirmDeleteAll(true)}
                  disabled={isDeletingAll}
                >
                  {isDeletingAll ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {t("notifications.deleteAll")}
                </Button>
              </div>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto overscroll-contain bg-[#FAFCFE]" style={{ WebkitOverflowScrolling: 'touch' }}>
            {isLoading ? (
              /* Mismo relleno y mismo alto de fila que la lista de verdad (ver
                 más abajo): el desplegable abre ya con su tamaño en vez de
                 abrir con 112 px de aro y saltar al llegar los avisos. */
              <div className="p-2.5">
                <SkeletonList rows={3} height={64} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <Bell className="h-6 w-6 text-muted-foreground/50" />
                </div>
                <p className="text-[13px] font-semibold text-foreground/80">{t("notifications.empty")}</p>
              </div>
            ) : (
              <div className="p-2.5 space-y-2">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDelete={deleteNotification}
                    getRelativeTime={getRelativeTime}
                  />
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notifications.deleteAll")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("notifications.confirmDeleteAll")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("notifications.deleteAll")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
